import React, { useState, useEffect } from 'react'
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Collapse
} from '@mui/material'
import { API_BASE_URL } from '../config/api'
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  Settings,
  Agriculture,
  Store,
  Logout,
  Assessment,
  Assignment,
  Article,
  Category,
  TrackChanges,
  Group,
  Gavel,
  Inventory,
  Chat,
  ShoppingCart,
  LocalOffer,
  Campaign,
  ExpandLess,
  ExpandMore,
  AccountCircle,
  ExitToApp
} from '@mui/icons-material'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@/hooks/redux'
import { logout } from '@/store/slices/authSlice'

import logo from '@/assets/famalink.webp'

const drawerWidth = 240

// Base menu items for all users
const baseMenuItems = [
  { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
  { text: 'Users', icon: <People />, path: '/users' },
  { text: 'Productions', icon: <Agriculture />, path: '/productions' },
  { text: 'Marketplace', icon: <Store />, path: '/marketplace' },
  { text: 'Lots', icon: <Inventory />, path: '/lots' },
  { text: 'Auctions', icon: <Gavel />, path: '/auctions' },
  { text: 'All Bids', icon: <LocalOffer />, path: '/all-bids' },
  { text: 'All Orders', icon: <ShoppingCart />, path: '/all-orders' },
  { text: 'Messages', icon: <Chat />, path: '/messages' },
  { text: 'Communication', icon: <Campaign />, path: '/communication' },
  { text: 'Reports', icon: <Assessment />, path: '/reports' },
  { text: 'Settings', icon: <Settings />, path: '/settings' }
];

// Center-specific menu items (only shown when logged into a center)
const centerSpecificMenuItems = [
  { text: 'Productions', icon: <Agriculture />, path: '/productions' },
  { text: 'Marketplace', icon: <Store />, path: '/marketplace' },
  { text: 'Lots', icon: <Inventory />, path: '/lots' },
  { text: 'Auctions', icon: <Gavel />, path: '/auctions' },
  { text: 'All Bids', icon: <LocalOffer />, path: '/all-bids' },
  { text: 'All Orders', icon: <ShoppingCart />, path: '/all-orders' },
  { text: 'Messages', icon: <Chat />, path: '/messages' },
  { text: 'Communication', icon: <Campaign />, path: '/communication' },
  { text: 'Reports', icon: <Assessment />, path: '/reports' },
  { text: 'Settings', icon: <Settings />, path: '/settings' }
];

// Production Guides menu for Super Admin when logged into a center
const productionGuidesMenuItem = [
  { text: 'Production Guides', icon: <Assignment />, path: '/production-guides' }
];

// Superadmin-specific menu items (shown at top)
const superadminMenuItems = [
  { text: 'Centres', icon: <Store />, path: '/centres' },
  { text: 'Buyer Requirements', icon: <Assignment />, path: '/buyer-requirements' },
];

// Article management submenu — collapsible under Articles
const articlesSubItems = [
  { text: 'Tags', icon: <LocalOffer />, path: '/tags' },
  { text: 'Categories', icon: <Category />, path: '/categories' },
];

// Advisor-specific menu items
const advisorMenuItems = [
  { text: 'My Farmers', icon: <Group />, path: '/my-farmers' },
  { text: 'Production Plans', icon: <Assignment />, path: '/production-plans' },
  { text: 'Progress Tracking', icon: <TrackChanges />, path: '/progress-tracking' }
];

interface LayoutProps {
  children: React.ReactNode
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [unreadCount, setUnreadCount] = useState(0)
  const [previousCount, setPreviousCount] = useState(0)
  const [articlesExpanded, setArticlesExpanded] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const user = useAppSelector((state) => state.auth.user)

  // Play notification sound
  const playNotificationSound = () => {
    const soundEnabled = localStorage.getItem('notificationSoundEnabled')
    if (soundEnabled === 'false') return

    const customSound = localStorage.getItem('customNotificationSound')
    const soundUrl = customSound || '/notification.mp3'
    
    const audio = new Audio(soundUrl)
    audio.volume = 0.5
    audio.play().catch(e => console.log('Audio play failed:', e))
  }

  // Fetch unread message count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const token = localStorage.getItem('token')
        if (!token) return

        const response = await fetch(`${API_BASE_URL}/support/admin/conversations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })

        if (response.ok) {
          const data = await response.json()
          const conversations = data.conversations || []
          const total = conversations.reduce((sum: number, conv: any) => sum + (conv.unread_count || 0), 0)
          
          // Play sound if count increased
          if (total > previousCount && previousCount > 0) {
            playNotificationSound()
          }
          
          setPreviousCount(total)
          setUnreadCount(total)
        }
      } catch (error) {
        console.error('Error fetching unread count:', error)
      }
    }

    fetchUnreadCount()
    const interval = setInterval(fetchUnreadCount, 10000) // Poll every 10 seconds
    return () => clearInterval(interval)
  }, [previousCount])

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = () => {
    dispatch(logout())
    handleMenuClose()
  }

  const handleExitImpersonation = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) return

      const response = await fetch(`${API_BASE_URL}/centres/exit-impersonation`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        
        // Update auth state with original superadmin credentials
        // Store the new token and user data
        localStorage.setItem('token', data.data.token)
        localStorage.setItem('user', JSON.stringify(data.data.user))
        
        // Dispatch login action with the superadmin data
        dispatch({ 
          type: 'auth/login', 
          payload: { 
            user: data.data.user, 
            token: data.data.token 
          } 
        })
        
        // Navigate to centres page
        navigate('/centres')
        
        // Force a page reload to ensure all state is refreshed
        window.location.href = '/centres'
      } else {
        console.error('Failed to exit impersonation:', await response.text())
      }
    } catch (error) {
      console.error('Error exiting impersonation:', error)
    }
  }

  const drawer = (
    <div>
      <Toolbar>
        <img 
          src={logo} 
          alt="FamaLink Logo" 
          style={{ height: '48px', width: 'auto' }}
        />
      </Toolbar>
      <Divider />
      <List>
        {/* Dashboard - always first */}
        <ListItem key="Dashboard" disablePadding>
          <ListItemButton
            selected={location.pathname === '/dashboard'}
            onClick={() => navigate('/dashboard')}
            sx={{
              '&.Mui-selected': {
                backgroundColor: 'primary.light',
                '&:hover': {
                  backgroundColor: 'primary.light',
                },
              },
            }}
          >
            <ListItemIcon sx={{ color: location.pathname === '/dashboard' ? 'primary.main' : 'inherit' }}>
              <Dashboard />
            </ListItemIcon>
            <ListItemText primary="Dashboard" />
          </ListItemButton>
        </ListItem>
        
        {/* Superadmin-specific menu items - shown at top after Dashboard (only when not impersonating) */}
        {user?.role === 'superadmin' && !user?.is_impersonating && superadminMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => navigate(item.path)}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.light',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                  },
                },
              }}
            >
              <ListItemIcon sx={{ color: location.pathname === item.path ? 'primary.main' : 'inherit' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}

        {/* Articles group — collapsible, contains Tags and Categories */}
        {user?.role === 'superadmin' && !user?.is_impersonating && (
          <>
            <ListItem disablePadding>
              <ListItemButton
                selected={location.pathname.startsWith('/articles') || location.pathname === '/tags' || location.pathname === '/categories'}
                onClick={() => setArticlesExpanded(!articlesExpanded)}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: 'primary.light',
                    '&:hover': {
                      backgroundColor: 'primary.light',
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ color: (location.pathname.startsWith('/articles') || location.pathname === '/tags' || location.pathname === '/categories') ? 'primary.main' : 'inherit' }}>
                  <Article />
                </ListItemIcon>
                <ListItemText primary="Articles" />
                {articlesExpanded ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={articlesExpanded} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {articlesSubItems.map((item) => (
                  <ListItem key={item.text} disablePadding>
                    <ListItemButton
                      selected={location.pathname === item.path}
                      onClick={() => navigate(item.path)}
                      sx={{
                        pl: 4,
                        '&.Mui-selected': {
                          backgroundColor: 'primary.light',
                          '&:hover': {
                            backgroundColor: 'primary.light',
                          },
                        },
                      }}
                    >
                      <ListItemIcon sx={{ color: location.pathname === item.path ? 'primary.main' : 'inherit' }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText primary={item.text} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}
        
        {/* Production Guides - shown for Super Admin when impersonating (logged into center) */}
        {user?.role === 'superadmin' && user?.is_impersonating && productionGuidesMenuItem.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => navigate(item.path)}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.light',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                  },
                },
              }}
            >
              <ListItemIcon sx={{ color: location.pathname === item.path ? 'primary.main' : 'inherit' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
        
        {/* Rest of base menu items - conditionally shown based on role and impersonation state */}
        {baseMenuItems.filter(item => {
          // For Super Admin: only show center-specific items when impersonating
          if (user?.role === 'superadmin') {
            if (user?.is_impersonating) {
              // When impersonating, show center-specific items (skip Dashboard)
              return item.text !== 'Dashboard' && centerSpecificMenuItems.some(ci => ci.text === item.text);
            } else {
              // When not impersonating, only show Users (skip Dashboard and center-specific items)
              return item.text === 'Users';
            }
          }
          // For other roles, show all items except Dashboard (already shown)
          return item.text !== 'Dashboard';
        }).map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => navigate(item.path)}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.light',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                  },
                },
              }}
            >
              <ListItemIcon sx={{ color: location.pathname === item.path ? 'primary.main' : 'inherit' }}>
                {item.text === 'Messages' && unreadCount > 0 ? (
                  <Badge badgeContent={unreadCount} color="error">
                    {item.icon}
                  </Badge>
                ) : (
                  item.icon
                )}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
        
        {/* Advisor-specific menu items */}
        {user?.role === 'advisor' && (
          <>
            <Divider sx={{ my: 1 }} />
            {advisorMenuItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => navigate(item.path)}
                  sx={{
                    '&.Mui-selected': {
                      backgroundColor: 'primary.light',
                      '&:hover': {
                        backgroundColor: 'primary.light',
                  },
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: location.pathname === item.path ? 'primary.main' : 'inherit' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </>
        )}
      </List>
    </div>
  )

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            FamaLink - Agricultural Marketplace Platform
            {user?.is_impersonating && user?.centre_name && (
              <Typography variant="caption" display="block" sx={{ fontSize: '0.75rem', opacity: 0.8 }}>
                Viewing: {user.centre_name} ({user.centre_code})
              </Typography>
            )}
          </Typography>
          {user?.is_impersonating && (
            <IconButton
              color="inherit"
              onClick={handleExitImpersonation}
              sx={{ mr: 2 }}
              title="Back to Super Admin"
            >
              <ExitToApp />
              <Typography variant="button" sx={{ ml: 1, display: { xs: 'none', md: 'inline' } }}>
                Back to Super Admin
              </Typography>
            </IconButton>
          )}
          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenuClick}
            color="inherit"
          >
            <Avatar sx={{ width: 32, height: 32 }}>
              <AccountCircle />
            </Avatar>
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{ flexGrow: 1, width: { sm: `calc(100% - ${drawerWidth}px)` } }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  )
}

export default Layout
